// ═══════════════════════════════════════════════════════════════
// AccountSettings.jsx - Logto Account API Integration
// Version: 2.0.0 - PLANIVIO BRANDING
// ═══════════════════════════════════════════════════════════════
// 
// ✅ v2.0: PLANIVIO Rebranding
//          - Farben: Purple (#6366F1) → Teal (#4A90A4)
//          - Gradient: Teal → Green
//          - Border/BG: Planivio Style
// ✅ v1.0: Original with Logto Account API
//
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';

const LOGTO_ENDPOINT = process.env.REACT_APP_LOGTO_ENDPOINT;

// ═══════════════════════════════════════════════════════════════
// PLANIVIO COLORS
// ═══════════════════════════════════════════════════════════════
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#F0F9FF',
  text: '#1F2937',
  textMuted: '#6B7280',
  shadow: 'rgba(74, 144, 164, 0.3)',
  // Semantic colors (keep functional)
  success: '#22C55E',
  successBg: '#F0FDF4',
  successBorder: '#BBF7D0',
  error: '#DC2626',
  errorBg: '#FEF2F2',
  errorBorder: '#FECACA',
  warning: '#F59E0B',
  warningBg: '#FFFBEB',
  warningBorder: '#FDE68A',
};

// ═══════════════════════════════════════════════════════════════
// STYLES - PLANIVIO BRANDING
// ═══════════════════════════════════════════════════════════════
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: `2px solid ${PLANIVIO.border}`,
  },
  header: {
    padding: '1.5rem',
    borderBottom: `2px solid ${PLANIVIO.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    backgroundColor: PLANIVIO.bgLight,
    zIndex: 10,
    borderRadius: '18px 18px 0 0',
  },
  headerTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: PLANIVIO.text,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  headerIcon: {
    width: '36px',
    height: '36px',
    background: PLANIVIO.gradient,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.1rem',
  },
  closeButton: {
    background: 'white',
    border: `2px solid ${PLANIVIO.border}`,
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: PLANIVIO.textMuted,
    padding: '0.5rem',
    borderRadius: '10px',
    transition: 'all 0.2s',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '1.5rem',
  },
  section: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: PLANIVIO.teal,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  avatarSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    background: PLANIVIO.bgCard,
    borderRadius: '12px',
    border: `2px solid ${PLANIVIO.borderLight}`,
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: PLANIVIO.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.75rem',
    color: 'white',
    overflow: 'hidden',
    border: '3px solid white',
    boxShadow: `0 4px 12px ${PLANIVIO.shadow}`,
    flexShrink: 0,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  inputGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: PLANIVIO.text,
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: `2px solid ${PLANIVIO.border}`,
    borderRadius: '10px',
    fontSize: '1rem',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#F9FAFB',
    color: PLANIVIO.text,
  },
  inputFocus: {
    borderColor: PLANIVIO.teal,
    boxShadow: `0 0 0 3px ${PLANIVIO.shadow}`,
  },
  inputDisabled: {
    backgroundColor: '#F3F4F6',
    color: PLANIVIO.textMuted,
    cursor: 'not-allowed',
  },
  button: {
    padding: '0.75rem 1.5rem',
    borderRadius: '10px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  buttonPrimary: {
    background: PLANIVIO.gradient,
    color: 'white',
    boxShadow: `0 2px 8px ${PLANIVIO.shadow}`,
  },
  buttonSecondary: {
    backgroundColor: 'white',
    color: PLANIVIO.teal,
    border: `2px solid ${PLANIVIO.border}`,
  },
  buttonDanger: {
    backgroundColor: PLANIVIO.errorBg,
    color: PLANIVIO.error,
    border: `2px solid ${PLANIVIO.errorBorder}`,
  },
  buttonSmall: {
    padding: '0.5rem 1rem',
    fontSize: '0.8rem',
  },
  buttonGroup: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  infoBox: {
    backgroundColor: PLANIVIO.bgLight,
    border: `2px solid ${PLANIVIO.border}`,
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  infoText: {
    fontSize: '0.875rem',
    color: PLANIVIO.teal,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorBox: {
    backgroundColor: PLANIVIO.errorBg,
    border: `2px solid ${PLANIVIO.errorBorder}`,
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  errorText: {
    fontSize: '0.875rem',
    color: PLANIVIO.error,
    margin: 0,
  },
  successBox: {
    backgroundColor: PLANIVIO.successBg,
    border: `2px solid ${PLANIVIO.successBorder}`,
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  successText: {
    fontSize: '0.875rem',
    color: PLANIVIO.success,
    margin: 0,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid ${PLANIVIO.borderLight}`,
    borderTopColor: PLANIVIO.teal,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  divider: {
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${PLANIVIO.border}, transparent)`,
    margin: '1.5rem 0',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    backgroundColor: PLANIVIO.bgCard,
    borderRadius: '12px',
    marginBottom: '0.75rem',
    border: `2px solid ${PLANIVIO.borderLight}`,
    transition: 'all 0.2s',
  },
  fieldInfo: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: PLANIVIO.text,
  },
  fieldValue: {
    fontSize: '0.875rem',
    color: PLANIVIO.textMuted,
    marginTop: '0.25rem',
  },
  editIcon: {
    fontSize: '1rem',
    color: PLANIVIO.teal,
    cursor: 'pointer',
  },
  // Password section - Planivio style
  passwordSection: {
    padding: '1.25rem',
    background: PLANIVIO.bgLight,
    borderRadius: '12px',
    border: `2px solid ${PLANIVIO.border}`,
  },
  passwordHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  passwordLabel: {
    fontWeight: '600',
    color: PLANIVIO.teal,
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  passwordDots: {
    fontSize: '0.85rem',
    color: PLANIVIO.textMuted,
  },
  passwordButton: {
    padding: '8px 16px',
    background: PLANIVIO.gradient,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '0.85rem',
    cursor: 'pointer',
    boxShadow: `0 2px 8px ${PLANIVIO.shadow}`,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s',
  },
  passwordButtonDisabled: {
    background: '#D1D5DB',
    cursor: 'default',
    boxShadow: 'none',
  },
  passwordInstructions: {
    background: 'white',
    borderRadius: '10px',
    padding: '12px',
    fontSize: '0.85rem',
    color: PLANIVIO.text,
    border: `1px solid ${PLANIVIO.borderLight}`,
  },
  passwordStep: {
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

// ═══════════════════════════════════════════════════════════════
// TEXTS
// ═══════════════════════════════════════════════════════════════
const texts = {
  de: {
    title: 'Konto-Einstellungen',
    profile: 'Profil',
    security: 'Sicherheit',
    name: 'Name',
    username: 'Benutzername',
    email: 'E-Mail',
    avatar: 'Profilbild',
    changeAvatar: 'Bild ändern',
    removeAvatar: 'Entfernen',
    password: 'Passwort',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    changePassword: 'Passwort ändern',
    save: 'Speichern',
    cancel: 'Abbrechen',
    edit: 'Bearbeiten',
    loading: 'Lade...',
    saving: 'Speichern...',
    saved: 'Gespeichert!',
    error: 'Fehler',
    success: 'Erfolgreich',
    profileUpdated: 'Profil wurde aktualisiert',
    passwordChanged: 'Passwort wurde geändert',
    passwordMismatch: 'Passwörter stimmen nicht überein',
    passwordTooShort: 'Passwort muss mindestens 8 Zeichen haben',
    enterAvatarUrl: 'Bild-URL eingeben',
    avatarUrlPlaceholder: 'https://example.com/bild.jpg',
    emailChangeInfo: 'E-Mail-Änderung erfordert Verifizierung per E-Mail.',
    passwordResetInfo: 'Klicke auf Ändern für Anleitung',
    sendResetEmail: 'Anleitung anzeigen',
    resetEmailSent: 'So geht\'s:',
    resetStep1: 'Melde dich ab (Logout)',
    resetStep2: 'Klicke "Passwort vergessen?" beim Login',
    resetStep3: 'Folge dem E-Mail-Link',
    noChanges: 'Keine Änderungen',
  },
  en: {
    title: 'Account Settings',
    profile: 'Profile',
    security: 'Security',
    name: 'Name',
    username: 'Username',
    email: 'Email',
    avatar: 'Profile Picture',
    changeAvatar: 'Change Picture',
    removeAvatar: 'Remove',
    password: 'Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    changePassword: 'Change Password',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    loading: 'Loading...',
    saving: 'Saving...',
    saved: 'Saved!',
    error: 'Error',
    success: 'Success',
    profileUpdated: 'Profile has been updated',
    passwordChanged: 'Password has been changed',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    enterAvatarUrl: 'Enter image URL',
    avatarUrlPlaceholder: 'https://example.com/image.jpg',
    emailChangeInfo: 'Email change requires verification via email.',
    passwordResetInfo: 'Click Change for instructions',
    sendResetEmail: 'Show instructions',
    resetEmailSent: 'How to:',
    resetStep1: 'Log out of the app',
    resetStep2: 'Click "Forgot password?" at login',
    resetStep3: 'Follow the email link',
    noChanges: 'No changes',
  }
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════
const AccountSettings = ({ isOpen, onClose, language = 'de', user }) => {
  const { getAccessToken } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Profile State
  const [profile, setProfile] = useState({
    name: '',
    username: '',
    avatar: '',
  });
  const [originalProfile, setOriginalProfile] = useState({});
  const [editingField, setEditingField] = useState(null);
  
  // Avatar URL Input
  const [showAvatarInput, setShowAvatarInput] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  
  // Password Reset
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  
  const t = texts[language] || texts.de;
  
  // ═══════════════════════════════════════════════════════════
  // API CALLS
  // ═══════════════════════════════════════════════════════════
  
  // Fetch user profile from Logto Account API
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // WICHTIG: Für Account API KEINEN Resource Parameter!
      const token = await getAccessToken();
      
      console.log('🔑 Got opaque token for Account API');
      
      const response = await fetch(`${LOGTO_ENDPOINT}/api/my-account`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Account API Error:', response.status, errorText);
        throw new Error(`Failed to fetch profile: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📋 Logto Profile:', data);
      
      const profileData = {
        name: data.name || '',
        username: data.username || '',
        avatar: data.avatar || '',
      };
      
      setProfile(profileData);
      setOriginalProfile(profileData);
      
    } catch (err) {
      console.error('❌ Fetch profile error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);
  
  // Update profile via Logto Account API
  const updateProfile = async (updates) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const token = await getAccessToken();
      
      const response = await fetch(`${LOGTO_ENDPOINT}/api/my-account`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Update Error:', response.status, errorData);
        throw new Error(errorData.message || `Update failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Profile updated:', data);
      
      setProfile(prev => ({ ...prev, ...updates }));
      setOriginalProfile(prev => ({ ...prev, ...updates }));
      setSuccess(t.profileUpdated);
      setEditingField(null);
      
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('❌ Update profile error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Load profile on open
  useEffect(() => {
    if (isOpen) {
      fetchProfile();
      setError(null);
      setSuccess(null);
      setEditingField(null);
      setShowAvatarInput(false);
      setPasswordResetSent(false);
    }
  }, [isOpen, fetchProfile]);
  
  // ═══════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════
  
  const handleSaveField = (field) => {
    if (profile[field] !== originalProfile[field]) {
      updateProfile({ [field]: profile[field] });
    } else {
      setEditingField(null);
    }
  };
  
  const handleCancelEdit = (field) => {
    setProfile(prev => ({ ...prev, [field]: originalProfile[field] }));
    setEditingField(null);
  };
  
  const handleAvatarSave = () => {
    if (avatarUrl.trim()) {
      updateProfile({ avatar: avatarUrl.trim() });
      setShowAvatarInput(false);
      setAvatarUrl('');
    }
  };
  
  const handleAvatarRemove = () => {
    updateProfile({ avatar: '' });
  };
  
  const handlePasswordReset = () => {
    setPasswordResetSent(true);
  };
  
  const handleClose = () => {
    setEditingField(null);
    setShowAvatarInput(false);
    setError(null);
    setSuccess(null);
    onClose();
  };
  
  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  
  if (!isOpen) return null;
  
  const getInitials = (name) => {
    if (!name) return '👤';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <div style={styles.headerIcon}>⚙️</div>
            {t.title}
          </div>
          <button 
            style={styles.closeButton}
            onClick={handleClose}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = PLANIVIO.teal;
              e.currentTarget.style.background = PLANIVIO.bgLight;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = PLANIVIO.border;
              e.currentTarget.style.background = 'white';
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Content */}
        <div style={styles.content}>
          {/* Error */}
          {error && (
            <div style={styles.errorBox}>
              <p style={styles.errorText}>❌ {error}</p>
            </div>
          )}
          
          {/* Success */}
          {success && (
            <div style={styles.successBox}>
              <p style={styles.successText}>✅ {success}</p>
            </div>
          )}
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{
                ...styles.spinner,
                margin: '0 auto 1rem',
              }} />
              <p style={{ color: PLANIVIO.textMuted }}>{t.loading}</p>
            </div>
          ) : (
            <>
              {/* ═══════════════════════════════════════════════ */}
              {/* PROFILE SECTION */}
              {/* ═══════════════════════════════════════════════ */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <span>👤</span> {t.profile}
                </div>
                
                {/* Avatar */}
                <div style={styles.avatarSection}>
                  <div style={styles.avatar}>
                    {profile.avatar ? (
                      <img 
                        src={profile.avatar} 
                        alt="Avatar" 
                        style={styles.avatarImage}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      getInitials(profile.name || user?.name)
                    )}
                  </div>
                  <div style={styles.avatarButtons}>
                    <button
                      style={{ 
                        ...styles.button, 
                        ...styles.buttonSecondary, 
                        ...styles.buttonSmall 
                      }}
                      onClick={() => setShowAvatarInput(!showAvatarInput)}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = PLANIVIO.teal;
                        e.currentTarget.style.background = PLANIVIO.bgLight;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = PLANIVIO.border;
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      📷 {t.changeAvatar}
                    </button>
                    {profile.avatar && (
                      <button
                        style={{ 
                          ...styles.button, 
                          ...styles.buttonDanger, 
                          ...styles.buttonSmall 
                        }}
                        onClick={handleAvatarRemove}
                        disabled={saving}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#FEE2E2';
                          e.currentTarget.style.borderColor = PLANIVIO.error;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = PLANIVIO.errorBg;
                          e.currentTarget.style.borderColor = PLANIVIO.errorBorder;
                        }}
                      >
                        🗑️ {t.removeAvatar}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Avatar URL Input */}
                {showAvatarInput && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>{t.enterAvatarUrl}</label>
                      <input
                        type="url"
                        style={styles.input}
                        placeholder={t.avatarUrlPlaceholder}
                        value={avatarUrl}
                        onChange={(e) => setAvatarUrl(e.target.value)}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = PLANIVIO.teal;
                          e.currentTarget.style.boxShadow = `0 0 0 3px ${PLANIVIO.shadow}`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = PLANIVIO.border;
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        style={{ 
                          ...styles.button, 
                          ...styles.buttonPrimary, 
                          ...styles.buttonSmall 
                        }}
                        onClick={handleAvatarSave}
                        disabled={!avatarUrl.trim() || saving}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = `0 4px 12px ${PLANIVIO.shadow}`;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${PLANIVIO.shadow}`;
                        }}
                      >
                        {saving ? t.saving : `💾 ${t.save}`}
                      </button>
                      <button
                        style={{ 
                          ...styles.button, 
                          ...styles.buttonSecondary, 
                          ...styles.buttonSmall 
                        }}
                        onClick={() => { setShowAvatarInput(false); setAvatarUrl(''); }}
                      >
                        {t.cancel}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Name Field */}
                <div 
                  style={styles.fieldRow}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = PLANIVIO.teal;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = PLANIVIO.borderLight;
                  }}
                >
                  <div style={styles.fieldInfo}>
                    <div style={styles.fieldLabel}>{t.name}</div>
                    {editingField === 'name' ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <input
                          type="text"
                          style={{ ...styles.input, marginBottom: '0.5rem' }}
                          value={profile.name}
                          onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                          autoFocus
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = PLANIVIO.teal;
                            e.currentTarget.style.boxShadow = `0 0 0 3px ${PLANIVIO.shadow}`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = PLANIVIO.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            style={{ ...styles.button, ...styles.buttonPrimary, ...styles.buttonSmall }}
                            onClick={() => handleSaveField('name')}
                            disabled={saving}
                          >
                            {saving ? t.saving : `💾 ${t.save}`}
                          </button>
                          <button
                            style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                            onClick={() => handleCancelEdit('name')}
                          >
                            {t.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.fieldValue}>{profile.name || '-'}</div>
                    )}
                  </div>
                  {editingField !== 'name' && (
                    <button
                      style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                      onClick={() => setEditingField('name')}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = PLANIVIO.teal;
                        e.currentTarget.style.background = PLANIVIO.bgLight;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = PLANIVIO.border;
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      ✏️ {t.edit}
                    </button>
                  )}
                </div>
                
                {/* Username Field */}
                <div 
                  style={styles.fieldRow}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = PLANIVIO.teal;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = PLANIVIO.borderLight;
                  }}
                >
                  <div style={styles.fieldInfo}>
                    <div style={styles.fieldLabel}>{t.username}</div>
                    {editingField === 'username' ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <input
                          type="text"
                          style={{ ...styles.input, marginBottom: '0.5rem' }}
                          value={profile.username}
                          onChange={(e) => setProfile(prev => ({ ...prev, username: e.target.value }))}
                          autoFocus
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = PLANIVIO.teal;
                            e.currentTarget.style.boxShadow = `0 0 0 3px ${PLANIVIO.shadow}`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = PLANIVIO.border;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            style={{ ...styles.button, ...styles.buttonPrimary, ...styles.buttonSmall }}
                            onClick={() => handleSaveField('username')}
                            disabled={saving}
                          >
                            {saving ? t.saving : `💾 ${t.save}`}
                          </button>
                          <button
                            style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                            onClick={() => handleCancelEdit('username')}
                          >
                            {t.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.fieldValue}>{profile.username || '-'}</div>
                    )}
                  </div>
                  {editingField !== 'username' && (
                    <button
                      style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                      onClick={() => setEditingField('username')}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = PLANIVIO.teal;
                        e.currentTarget.style.background = PLANIVIO.bgLight;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = PLANIVIO.border;
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      ✏️ {t.edit}
                    </button>
                  )}
                </div>
                
                {/* Email Field (Read-only with info) */}
                <div style={styles.fieldRow}>
                  <div style={styles.fieldInfo}>
                    <div style={styles.fieldLabel}>{t.email}</div>
                    <div style={styles.fieldValue}>{user?.email || '-'}</div>
                  </div>
                  <span style={{ 
                    fontSize: '1.25rem', 
                    color: PLANIVIO.textMuted,
                    background: PLANIVIO.bgLight,
                    padding: '4px 8px',
                    borderRadius: '6px',
                  }}>🔒</span>
                </div>
                
                <div style={styles.infoBox}>
                  <p style={styles.infoText}>
                    <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
                    {t.emailChangeInfo}
                  </p>
                </div>
              </div>
              
              <div style={styles.divider} />
              
              {/* ═══════════════════════════════════════════════ */}
              {/* SECURITY SECTION */}
              {/* ═══════════════════════════════════════════════ */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <span>🔐</span> {t.security}
                </div>
                
                {/* Password - Planivio Style */}
                <div style={styles.passwordSection}>
                  <div style={styles.passwordHeader}>
                    <div>
                      <div style={styles.passwordLabel}>
                        <span>🔑</span> {t.password}
                      </div>
                      <div style={styles.passwordDots}>••••••••••</div>
                    </div>
                    <button
                      onClick={handlePasswordReset}
                      disabled={passwordResetSent}
                      style={{
                        ...styles.passwordButton,
                        ...(passwordResetSent ? styles.passwordButtonDisabled : {})
                      }}
                      onMouseOver={(e) => {
                        if (!passwordResetSent) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = `0 4px 12px ${PLANIVIO.shadow}`;
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!passwordResetSent) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = `0 2px 8px ${PLANIVIO.shadow}`;
                        }
                      }}
                    >
                      {passwordResetSent ? '✅ Anleitung' : '🔄 Ändern'}
                    </button>
                  </div>
                  
                  {passwordResetSent && (
                    <div style={styles.passwordInstructions}>
                      <div style={{ fontWeight: '600', marginBottom: '10px', color: PLANIVIO.teal }}>
                        📋 {t.resetEmailSent}
                      </div>
                      <div style={styles.passwordStep}>
                        <span style={{ 
                          background: PLANIVIO.gradient, 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: '22px', 
                          height: '22px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                        }}>1</span>
                        {t.resetStep1}
                      </div>
                      <div style={styles.passwordStep}>
                        <span style={{ 
                          background: PLANIVIO.gradient, 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: '22px', 
                          height: '22px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                        }}>2</span>
                        {t.resetStep2}
                      </div>
                      <div style={styles.passwordStep}>
                        <span style={{ 
                          background: PLANIVIO.gradient, 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: '22px', 
                          height: '22px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                        }}>3</span>
                        {t.resetStep3}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Loading Overlay */}
        {saving && (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner} />
          </div>
        )}
      </div>
      
      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AccountSettings;

